import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { Phone, MessageCircle, Mail, MapPin, Loader2 } from "lucide-react";
import { BUSINESS } from "@/App";

const PROJECT_TYPES = [
  "Interior Repainting",
  "Exterior Repainting",
  "Full Home Repaint",
  "Accent Wall Refresh",
  "Damp-Patch Repainting",
  "Not sure yet",
];

export default function ContactForm({ prefill = {} }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    project_type: prefill.project_type || "",
    area: prefill.area || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.project_type) {
      toast.error("Please fill name, phone and project type.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createLead({
        ...form,
        source: "contact-section",
        estimated_cost: prefill.estimated_cost,
      });
      toast.success(
        res?.promo_code
          ? `Thanks! Your 25% code: ${res.promo_code}. We'll WhatsApp you shortly.`
          : "Thanks! We'll call you within 30 minutes."
      );
      setForm({ name: "", phone: "", project_type: "", area: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16">
          {/* Info */}
          <div>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-orange-600">
              Book a site visit
            </div>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink">
              Talk to a paint expert today.
            </h2>
            <p className="mt-4 text-ink-2 max-w-md">
              Share a few details and we&apos;ll call back with a transparent estimate and
              the earliest available slot.
            </p>

            <div className="mt-8 space-y-3">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="flex items-center gap-3 p-4 rounded-xl hairline bg-white hover:bg-orange-50/60 transition-colors"
                data-testid="contact-info-phone"
              >
                <div className="h-10 w-10 rounded-xl bg-orange-50 grid place-items-center">
                  <Phone className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-ink font-bold">
                    Call us
                  </div>
                  <div className="font-bold text-ink">{BUSINESS.phone}</div>
                </div>
              </a>
              <a
                href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(
                  "Hi, I'd like a painting quote."
                )}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-4 rounded-xl hairline bg-white hover:bg-orange-50/60 transition-colors"
                data-testid="contact-info-whatsapp"
              >
                <div className="h-10 w-10 rounded-xl bg-green-50 grid place-items-center">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-ink font-bold">
                    WhatsApp
                  </div>
                  <div className="font-bold text-ink">Chat instantly</div>
                </div>
              </a>
              <div className="flex items-center gap-3 p-4 rounded-xl hairline bg-white">
                <div className="h-10 w-10 rounded-xl bg-orange-50 grid place-items-center">
                  <Mail className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-ink font-bold">
                    Email
                  </div>
                  <div className="font-bold text-ink">{BUSINESS.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-xl hairline bg-white">
                <div className="h-10 w-10 rounded-xl bg-orange-50 grid place-items-center">
                  <MapPin className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-ink font-bold">
                    Service area
                  </div>
                  <div className="font-bold text-ink">All of Bangalore, India</div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="rounded-3xl bg-[#FAFAFA] hairline p-6 md:p-8 shadow-soft"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cf-name" className="text-xs font-bold text-ink">
                  Full name
                </Label>
                <Input
                  id="cf-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="e.g., Priya Sharma"
                  className="mt-1.5 h-12 rounded-xl bg-white"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <Label htmlFor="cf-phone" className="text-xs font-bold text-ink">
                  Phone number
                </Label>
                <Input
                  id="cf-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98xxx xxxxx"
                  className="mt-1.5 h-12 rounded-xl bg-white"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-ink">Project type</Label>
                <Select
                  value={form.project_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}
                >
                  <SelectTrigger
                    className="mt-1.5 h-12 rounded-xl bg-white"
                    data-testid="contact-select-project-type"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`contact-opt-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cf-area" className="text-xs font-bold text-ink">
                  Area (sq.ft)
                </Label>
                <Input
                  id="cf-area"
                  value={form.area}
                  onChange={update("area")}
                  placeholder="e.g., 1200"
                  className="mt-1.5 h-12 rounded-xl bg-white"
                  data-testid="contact-input-area"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="cf-msg" className="text-xs font-bold text-ink">
                Project details (optional)
              </Label>
              <Textarea
                id="cf-msg"
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your space, timeline, colour ideas..."
                className="mt-1.5 rounded-xl bg-white"
                data-testid="contact-input-message"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="lead-form-submit"
              className="mt-6 w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-brand"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                "Request Free Site Visit"
              )}
            </Button>
            <div className="mt-3 text-[11px] text-muted-ink text-center">
              By submitting, you agree to be contacted by {BUSINESS.name}.
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
